import React, { useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';

import { getSuggestions } from '@/store/actions/payout';
import { selectSuggestions } from '@/store/selectors/payout';
import useLanguage from '@/components/common/hooks/useLanguage';
import BottomSheet from '@/components/payout/BottomSheet';
import { Currency, ExpenseSuggestion } from '@/store/reducers/payout';
import { formatMoney } from '@/utils/money';
import { ATTACHMENT_ACCEPT, formatSize } from '@/utils/attachments';
import { PaperclipIcon, usePickedFiles } from '@/components/payout/Attachments';

interface Props {
  onAdd: (
    body: { title: string, amount: number, currency: Currency, isRecurring: boolean },
    files: File[],
  ) => void;
  onClose: () => void;
}

/**
 * Suggestions come from the server already scoped to the caller: an employee is
 * offered only the expense types they have used before, an admin every type in
 * the system. The list is ordered by most recently used, and the search box
 * doubles as the field for a title that has never been used.
 */
const AddExpense: React.FC<Props> = ({ onAdd, onClose }) => {
  const dispatch = useDispatch();
  const suggestions = useSelector(selectSuggestions);
  const [{ payout, common }] = useLanguage();

  const [title, setTitle] = useState('');
  const [amount, setAmount] = useState('');
  const [currency, setCurrency] = useState<Currency>('USD');
  const [isRecurring, setIsRecurring] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const fileInput = useRef<HTMLInputElement>(null);
  const { accept, error: fileError } = usePickedFiles();

  // Previews for the picked images, released when the pick changes or the sheet
  // closes.
  const previews = useMemo(
    () => files.map((file) => (file.type.startsWith('image/') ? URL.createObjectURL(file) : '')),
    [files],
  );
  useEffect(() => () => previews.forEach((url) => url && URL.revokeObjectURL(url)), [previews]);

  useEffect(() => {
    dispatch(getSuggestions(title.trim() || undefined));
  }, [title]);

  const parsedAmount = Number(amount.replace(',', '.'));
  const canSubmit = Boolean(title.trim()) && parsedAmount > 0;

  const handleSubmit = () => {
    if (!canSubmit) return;
    onAdd({
      title: title.trim(), amount: parsedAmount, currency, isRecurring,
    }, files);
    onClose();
  };

  // Picking a suggestion prefills the amount it was last used with, which is
  // right nearly every time for a subscription -- and its currency with it, or a
  // hryvnia figure would be prefilled as dollars.
  const pick = (suggestion: ExpenseSuggestion) => {
    setTitle(suggestion.title);
    if (!amount) {
      setAmount(String(suggestion.lastAmount));
      setCurrency(suggestion.lastCurrency || 'USD');
    }
  };

  return (
    <BottomSheet onClose={onClose}>
      <Heading>{payout.add_expense}</Heading>

        <Input
          autoFocus
          value={title}
          placeholder={payout.search_expenses}
          onChange={(e) => setTitle(e.target.value)}
        />

        {Boolean(suggestions && suggestions.length) && (
          <>
            <Label>{payout.recently_used}</Label>
            <Chips>
              {suggestions!.slice(0, 12).map((suggestion) => (
                <Chip key={suggestion.title} onClick={() => pick(suggestion)}>
                  {suggestion.title}
                  <ChipAmount>
                    {formatMoney(suggestion.lastAmount, suggestion.lastCurrency)}
                  </ChipAmount>
                </Chip>
              ))}
            </Chips>
          </>
        )}

        <Label>{payout.amount}</Label>
        <AmountRow>
          <Input
            value={amount}
            inputMode="decimal"
            placeholder="0"
            onChange={(e) => setAmount(e.target.value)}
          />
          {/* Hryvnia is kept as hryvnia and shown beside the dollars, never converted. */}
          <Currencies>
            {(['USD', 'UAH'] as Currency[]).map((option) => (
              <CurrencyOption
                key={option}
                selected={currency === option}
                onClick={() => setCurrency(option)}
              >
                {option === 'USD' ? '$' : '₴'}
              </CurrencyOption>
            ))}
          </Currencies>
        </AmountRow>

        <Repeat onClick={() => setIsRecurring(!isRecurring)}>
          <Box checked={isRecurring} />
          {payout.repeat_every_month}
        </Repeat>

        <Label>{payout.proof_optional}</Label>
        {files.map((file, i) => (
          <Picked key={`${file.name}-${file.size}-${file.lastModified}`}>
            {previews[i] ? <PickedImage src={previews[i]} alt="" /> : <PickedKind>PDF</PickedKind>}
            <PickedName>{file.name}</PickedName>
            <PickedSize>{formatSize(file.size)}</PickedSize>
            <PickedRemove
              aria-label={payout.remove}
              onClick={() => setFiles(files.filter((_, j) => j !== i))}
            >
              ×
            </PickedRemove>
          </Picked>
        ))}
        <AttachButton onClick={() => fileInput.current && fileInput.current.click()}>
          <PaperclipIcon size={14} />
          {payout.screenshot_or_file}
        </AttachButton>
        {fileError && <FileError>{fileError}</FileError>}
        <input
          ref={fileInput}
          type="file"
          multiple
          accept={ATTACHMENT_ACCEPT}
          style={{ display: 'none' }}
          onChange={(e) => {
            setFiles([...files, ...accept(e.target.files, files.length)]);
            e.target.value = '';
          }}
        />

      <Buttons>
        <Secondary onClick={onClose}>{common.cancel}</Secondary>
        <Primary disabled={!canSubmit} onClick={handleSubmit}>
          {payout.add_expense}
        </Primary>
      </Buttons>
    </BottomSheet>
  );
};



const Heading = styled.div`
  font-size: 18px;
  font-weight: 800;
  color: #21272e;
  margin-bottom: 16px;
`;

const Label = styled.div`
  text-transform: uppercase;
  font-size: 12px;
  font-weight: bold;
  letter-spacing: 1.3px;
  color: #909599;
  margin: 16px 0 8px;
`;

const Input = styled.input`
  width: 100%;
  height: 48px;
  border-radius: 6px;
  border: solid 1px #dae1e8;
  padding: 0 12px;
  font-size: 14px;
  color: #21272e;
  outline: none;

  :focus {
    border-color: #fb7e14;
  }
`;

const AmountRow = styled.div`
  display: flex;
`;

const Currencies = styled.div`
  display: flex;
  flex-shrink: 0;
  margin-left: 8px;
  border: solid 1px #dae1e8;
  border-radius: 6px;
  overflow: hidden;
`;

const CurrencyOption = styled.button<{ selected: boolean }>`
  width: 48px;
  height: 46px;
  border: none;
  font-size: 16px;
  font-weight: 700;
  cursor: pointer;
  color: ${({ selected }) => (selected ? '#ffffff' : '#21272e')};
  background: ${({ selected }) => (selected ? '#fb7e14' : '#ffffff')};
`;

const Chips = styled.div`
  display: flex;
  flex-wrap: wrap;
`;

const Chip = styled.div`
  display: flex;
  align-items: center;
  font-size: 12px;
  font-weight: 600;
  color: #21272e;
  background: #f0f1f2;
  border-radius: 6px;
  padding: 8px 10px;
  margin: 0 6px 6px 0;
  cursor: pointer;
`;

const ChipAmount = styled.span`
  color: #909599;
  margin-left: 6px;
`;

const Repeat = styled.div`
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #21272e;
  margin-top: 20px;
  cursor: pointer;
  user-select: none;
`;

const Box = styled.div<{ checked: boolean }>`
  width: 20px;
  height: 20px;
  border-radius: 4px;
  margin-right: 10px;
  border: solid 2px ${({ checked }) => (checked ? '#fb7e14' : '#dae1e8')};
  background: ${({ checked }) => (checked ? '#fb7e14' : 'transparent')};
`;

const Picked = styled.div`
  display: flex;
  align-items: center;
  padding: 6px 0;
  border-bottom: solid 1px #eceff2;
`;

const PickedImage = styled.img`
  width: 36px;
  height: 36px;
  border-radius: 4px;
  object-fit: cover;
  flex-shrink: 0;
`;

const PickedKind = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 4px;
  background: #f7f8f9;
  border: solid 1px #dae1e8;
  color: #d9534f;
  font-size: 10px;
  font-weight: 800;
  line-height: 34px;
  text-align: center;
  flex-shrink: 0;
`;

const PickedName = styled.div`
  flex: 1;
  min-width: 0;
  margin: 0 10px;
  font-size: 13px;
  color: #21272e;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const PickedSize = styled.div`
  font-size: 12px;
  color: #909599;
  white-space: nowrap;
`;

const PickedRemove = styled.button`
  width: 32px;
  height: 32px;
  margin-left: 4px;
  border: none;
  background: transparent;
  color: #909599;
  font-size: 20px;
  cursor: pointer;
`;

const AttachButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 44px;
  margin-top: 8px;
  border: dashed 1px #fb7e14;
  border-radius: 6px;
  background: #fff8f1;
  color: #fb7e14;
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;

  svg {
    margin-right: 6px;
  }
`;

const FileError = styled.div`
  font-size: 12px;
  color: #d9534f;
  margin-top: 6px;
`;

const Buttons = styled.div`
  display: flex;
  margin-top: 24px;
`;

const Primary = styled.button`
  flex: 1;
  height: 48px;
  border: none;
  border-radius: 6px;
  background: #fb7e14;
  color: #ffffff;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;

  :disabled {
    background: #dae1e8;
    cursor: default;
  }
`;

const Secondary = styled.button`
  flex: 1;
  height: 48px;
  margin-right: 12px;
  border-radius: 6px;
  border: solid 1px #dae1e8;
  background: #ffffff;
  color: #21272e;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
`;

export default AddExpense;
